import { CalendarIcon, ClockIcon } from "@xola/icons";
import { Alert, Checkbox, formatDate, FormGroup, Input, Label } from "@xola/ui-kit";
import {
    isEquipmentBased,
    isMembershipProduct,
    isMerchandiseProduct,
    isPartnerPurchase,
    isPurchaseItemCanceled,
    SecondaryButton,
} from "common";
import { find, floor, isEmpty, isNil } from "lodash";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    Affiliate,
    AffiliateDepositAmountType,
    AffiliateStatus,
    AnyRewardCode,
    ApplyCode,
    Coupon,
    CouponCodeWithCoupon,
    CouponSource,
    CouponType,
    DelegateRole,
    Experience,
    ExperiencePurchaseItem,
    isMembershipCode,
    LineItemType,
    Package,
    Product,
    Purchase,
    PurchaseItemObject,
    PurchaseRuleLinkEntityObject,
    Source,
    User,
    UserRole,
    UserType,
} from "types";
import { useDebounce } from "use-debounce";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { ApplyCodes, CodeSummary } from "../Coupon/ApplyCodes";
import { Loader } from "../Loader";
import { getProductRestrictions } from "../Product/EditGiftOfferRestrictions";
import { usePurchaseResponseStore } from "../PurchaseCreate/store/usePurchaseResponseStore";
import { AffiliateVoucherForm } from "./AffiliateVoucherForm";
import { api } from "@/api/api";
import { useProduct } from "@/hooks/resources/useProduct";
import { useUser } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { Item, SelectionState, useSelection } from "@/hooks/useSelection";
import { canApplyBXGYCode, getCouponProductRestrictions, isPaymentCoupon } from "@/models/Coupon";
import { isAffiliatePurchase, isPackagePurchase } from "@/models/Purchase";
import { getDemographicLineItems, getEquipmentRosterLineItems, isCodeSupported } from "@/models/PurchaseItem";
import { isAffiliate } from "@/models/User";
import { CreatePurchaseItemState, DemographicLineItem, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { canApplyCoupons, canShowAffiliates } from "@/stores/globalStore";

interface ApplyCodeFormProps {
    onSelectCode: (selectedCode?: ApplyCode, selectionState?: SelectionState) => void;
    onSelectAmount: (amount: number | undefined) => void;
    onSelectVoucher: (voucher: string) => void;
    onInputChange?: (value?: string) => void;
    onApply?: (e: any) => Promise<void>;
    isLoading?: boolean;
    purchase?: Purchase;
    item?: ExperiencePurchaseItem | CreatePurchaseItemState;
}

export const ApplyCodeForm = ({
    onSelectCode,
    onSelectAmount,
    onSelectVoucher,
    onInputChange,
    onApply,
    isLoading = false,
    purchase: completedPurchase,
    item,
}: ApplyCodeFormProps) => {
    const defaultCode = useCreatePurchaseStore((state) => state.code);

    const isPurchasePackage = useCreatePurchaseStore((state) => isPackagePurchase(state.purchase));
    const shouldShowPrivacyWarning = useCreatePurchaseStore((state) => state.showPrivacyWarning());
    const purchaseItemsFromStore = usePurchaseResponseStore((state) => state.purchase.items);
    const items = completedPurchase?.items ?? purchaseItemsFromStore;
    const isPartnerBooking = usePurchaseResponseStore((state) => isPartnerPurchase(state.purchase as Purchase));
    const itemPrivacy = useCreatePurchaseStore((state) => state.getActiveItem()?.privacy);
    const privacy = item?.privacy ?? itemPrivacy;
    const isAffiliateBooking = usePurchaseResponseStore((state) => isAffiliatePurchase(state.purchase as Purchase));
    const isAffiliateOrder = completedPurchase ? isAffiliatePurchase(completedPurchase) : isAffiliateBooking;
    const packageId = useCreatePurchaseStore((state) => state.getPurchasePackageId());
    const purchaseItems = useMemo(
        () =>
            (items ?? [])?.map((item) => ({
                id: item.id,
                object: item.object,
                productId: item.product.id,
                arrivalDateTime: formatDate((item as ExperiencePurchaseItem).arrivalDateTime, "ddd ll"),
                isCanceled: isPurchaseItemCanceled(item),
            })),
        [items],
    );
    // A code on a canceled or declined item is still redeemed, against a reservation that will never be honoured
    const selectablePurchaseItems = useMemo(() => purchaseItems.filter((item) => !item.isCanceled), [purchaseItems]);

    const productId =
        !!item && isCodeSupported(item.object)
            ? item?.product.id
            : purchaseItems.find((item) => isCodeSupported(item?.object))?.productId;

    const { data: product } = useProduct(productId);

    const { data: pkg } = useProduct<Package>(packageId!, null, { enabled: isPurchasePackage });

    const user = useUser();

    const [selectedCode, setSelectedCode] = useState<ApplyCode | undefined>();
    const [searchValue, setSearchValue] = useState<string | undefined>();
    const [debouncedSearch] = useDebounce(searchValue, 500);
    const [amount, setAmount] = useState<number | undefined>();

    const [voucher, setVoucher] = useState("");

    const [defaultSelection] = useState<SelectionState>({ all: false, selected: [] });
    const selection = useSelection(purchaseItems as Item[], true, defaultSelection);

    const handleChangeAmount = (value: number | undefined) => {
        setAmount(value);
        onSelectAmount(value);
    };

    const handleVoucher = (value: string) => {
        setVoucher(value);
        onSelectVoucher(value);
    };

    const canViewAffiliateCodes = canShowAffiliates() && !isAffiliate(user);

    const { data: affiliates } = useAutoFetchCollection<Affiliate>(["affiliates"], null, {
        enabled: canViewAffiliateCodes,
    });
    const { mutateAsync: updatePurchase, isLoading: isLoadingApplyCodeDryRun } = useMutation(async (payload: any) => {
        const purchaseId = usePurchaseResponseStore.getState().purchase.id;
        const id = completedPurchase?.id ?? purchaseId;
        return api.patch<Purchase>(`/purchases/${id}`, payload, {
            params: { dryRun: true },
        });
    });

    const { data: coupons, isLoading: isCouponsLoading } = useCollection<Coupon>(
        "coupons",
        {
            limit: 20,
            source: Source.XOLA,
            "type[notEqual]": CouponType.VOUCHER,
        },
        { enabled: canApplyCoupons() },
    );

    const { data: voucherCoupons, isLoading: isVoucherCouponsLoading } = useCollection<Coupon>(
        "coupons",
        {
            limit: 20,
            source: Source.XOLA,
            type: CouponType.VOUCHER,
        },
        { enabled: canApplyCoupons() },
    );

    const couponMappedIds = coupons.map((coupon) => coupon.id).join(",");
    const voucherMappedIds = voucherCoupons.map((coupon) => coupon.id).join(",");

    const { data: codes, isLoading: isLoadingCouponCodes } = useCollection<CouponCodeWithCoupon>(
        ["codes"],
        {
            "coupon[in]": debouncedSearch ? undefined : couponMappedIds?.length > 0 ? couponMappedIds : undefined,
            ...(debouncedSearch && { "code[contains]": debouncedSearch }),
            expand: "coupon,purchaseRule,membershipItem",
        },
        { enabled: !isAffiliate(user) && !isCouponsLoading },
    );

    const { data: voucherCodesRaw, isLoading: isLoadingVoucherCouponCodes } = useCollection<CouponCodeWithCoupon>(
        ["codes"],
        {
            "coupon[in]": voucherMappedIds?.length > 0 ? voucherMappedIds : undefined,
            expand: "coupon,purchaseRule,membershipItem",
        },
        {
            enabled:
                !isAffiliate(user) && !isVoucherCouponsLoading && !!voucherCoupons?.length && !debouncedSearch?.length,
        },
    );

    const voucherCodes = useMemo(
        () => (debouncedSearch?.length ? [] : voucherCodesRaw ?? []),
        [debouncedSearch, voucherCodesRaw],
    );

    const showMistSelection =
        purchaseItems.length > 1 &&
        selectedCode &&
        !isPaymentCoupon(selectedCode.coupon) &&
        selectedCode?.object !== PurchaseRuleLinkEntityObject.AFFILIATE;

    const hideAffiliateCodes =
        isPartnerBooking ||
        isAffiliateOrder ||
        !canViewAffiliateCodes ||
        isMembershipProduct(product) ||
        isMerchandiseProduct(product);

    const activeAffiliates = useMemo(
        () => (hideAffiliateCodes ? [] : affiliates.filter((agent) => agent.status === AffiliateStatus.ACTIVE)),
        [hideAffiliateCodes, affiliates],
    );

    const isEquipmentExperience = Boolean(product) && isEquipmentBased(product as Experience);

    // BXGY qualifies on the unit the experience actually sells, which is equipment on an
    // equipment-based experience.
    const primaryUnitLineItemsFromStore = useCreatePurchaseStore((state) => {
        if (!state.getActiveItem()) {
            return [];
        }

        return isEquipmentExperience ? state.getEquipmentLineItems() : state.getDemographicLineItems();
    });

    const primaryUnitLineItems = item
        ? isEquipmentExperience
            ? getEquipmentRosterLineItems(item)
            : getDemographicLineItems(item)
        : primaryUnitLineItemsFromStore;

    const handleSelectCode = useCallback(
        async (selectedCode?: ApplyCode | null) => {
            if (!selectedCode) {
                setSelectedCode(undefined);
                setSearchValue(undefined);
                return;
            }

            const purchaseAmount = usePurchaseResponseStore.getState().amount;
            if (selectedCode.deposit?.enabled) {
                if (
                    [AffiliateDepositAmountType.ABSOLUTE, AffiliateDepositAmountType.CUSTOM].includes(
                        selectedCode.deposit.amountType,
                    )
                ) {
                    handleChangeAmount(selectedCode.deposit.amount);
                } else if (selectedCode.deposit?.amountType === AffiliateDepositAmountType.PERCENT) {
                    const payload = {
                        ops: [
                            {
                                path: "/",
                                op: "addCode",
                                data: {
                                    code: selectedCode.code,
                                },
                            },
                        ],
                    };
                    const { data: updatedPurchase } = await updatePurchase({ ...payload });
                    const depositLineItem = find(updatedPurchase.lineItems, { type: LineItemType.AFFILIATE_DEPOSIT });
                    const amount = depositLineItem?.amount ? -1 * depositLineItem.amount : purchaseAmount;
                    handleChangeAmount(amount);
                }
            } else if (selectedCode.voucher) {
                handleChangeAmount(purchaseAmount);
            }

            setSelectedCode(selectedCode);

            /**
             * Removed the `purchaseItems.length === 1 || selectedCode.experiences?.all || isPaymentCoupon(selectedCode.coupon)` condition in XS-1.
             *
             * Scenario:
             * When user is trying to apply a coupon code for MIST checkout, he selected a coupon code (COUPON1).
             * Now, the purchase summary gets refreshed and shows new balance with COUPON1 applied.
             * But, when he tries to change the coupon code from `COUPON1` to `COUPON2` the purchase summary does not get refreshed.
             * As purchaseItems would never be `1` for MIST, and other conditions are also not satisfying. So, we are not calling the `onSelectCode`, which is responsible for refreshing the payment summary
             */
            onSelectCode(selectedCode);

            /** When a coupon restricts to specific products, deselect any already-selected item the coupon does not support. Restriction is read from the purchaseRule only. */
            const restrictedProductIds = getCouponProductRestrictions(selectedCode.coupon);
            if (restrictedProductIds.length > 0) {
                for (const purchaseItem of purchaseItems) {
                    if (!purchaseItem.id || !("arrivalDateTime" in purchaseItem)) continue;

                    if (
                        !restrictedProductIds.includes(purchaseItem.productId) &&
                        selection.isSelected({ id: purchaseItem.id })
                    ) {
                        selection.select({ id: purchaseItem.id });
                    }
                }
            }
        },
        // Including all dependencies here would be excessive.
        // The remaining dependencies are function references and state setters
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [purchaseItems],
    );

    const quantityFromStore = useCreatePurchaseStore((state) => state.getActiveItem()?.quantity);
    const quantity = item ? item.quantity : quantityFromStore ?? 0;
    const filterCouponsRestrictions = useMemo(() => {
        if (!product) return [];
        return [...codes, ...voucherCodes].filter((code: AnyRewardCode) =>
            shouldIncludeCode(
                code,
                product,
                primaryUnitLineItems,
                quantity,
                purchaseItems,
                isMembershipProduct(product),
                isMerchandiseProduct(product),
                user,
                item,
                searchValue,
                pkg,
            ),
        );
    }, [product, codes, voucherCodes, primaryUnitLineItems, quantity, purchaseItems, searchValue, user, pkg, item]);

    const filteredCodes = filterCouponsRestrictions.map((code) => {
        const isMembership = isMembershipCode(code);
        const { coupon } = code;

        return {
            ...code,
            type: isMembership ? CouponType.DISCOUNT : coupon.type,
            amount: <CodeSummary code={code} />,
        };
    });

    // I dislike type conversion, but this is already happening implicitly in the ApplyCodes component and we need to make significant type changes to avoid this, the real object structure replicates the ApplyCode component one
    const enteredCode = [...filteredCodes, ...activeAffiliates].find(
        (code) => code.code === searchValue,
    ) as unknown as ApplyCode;

    const selectedCodeRestrictedProductIds = useMemo(
        () => getCouponProductRestrictions(selectedCode?.coupon),
        [selectedCode],
    );

    const handleApply = async () => {
        if (enteredCode) {
            // Find the matching code and pass it to the payload
            handleSelectCode(enteredCode);
        }

        await onApply?.(null);

        // Clear selected code once applied successfully.
        handleSelectCode(null);
    };

    useEffect(() => {
        onSelectCode(selectedCode, selection.state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.state.selected]);

    useEffect(() => {
        if (showMistSelection && selectedCode?.object === "affiliate") {
            // select all items for affiliate code in mist bookings except gift and membership items
            selection.setState({
                all: false,
                selected: selectablePurchaseItems
                    .filter(
                        (item) =>
                            item.object !== PurchaseItemObject.GIFT && item.object !== PurchaseItemObject.MEMBERSHIP,
                    )
                    .map((item) => item.id),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMistSelection, selectedCode]);

    const isDisabled =
        isLoading ||
        (!selectedCode && !searchValue) ||
        (selectedCode?.type === CouponType.UNLOCK && isNil(amount)) ||
        isLoadingApplyCodeDryRun;

    const handleUnlockAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = isEmpty(event.target.value) ? undefined : Number(event.target.value);

        handleChangeAmount(value);
    };

    const handleInputChange = useCallback(
        (searchValue: string) => {
            setSearchValue(searchValue);
            onInputChange?.(searchValue);
        },
        [onInputChange],
    );

    return (
        <div className="flex-1 space-y-2">
            <div className="flex flex-row items-center space-x-1">
                <div className="flex-1">
                    <FormGroup disabled={isLoadingCouponCodes || isLoadingVoucherCouponCodes}>
                        <Label>Code</Label>
                        <ApplyCodes
                            codes={isPartnerBooking ? [] : filterCouponsRestrictions}
                            affiliates={activeAffiliates}
                            inputValue={searchValue ?? defaultCode?.code}
                            onSelectCode={handleSelectCode}
                            onInputChange={handleInputChange}
                        />
                    </FormGroup>
                </div>
                {onApply && (
                    <SecondaryButton className="mt-1.5" color="secondary" disabled={isDisabled} onClick={handleApply}>
                        Apply Code
                    </SecondaryButton>
                )}
            </div>

            {selectedCode?.deposit?.enabled === false ||
            (selectedCode?.deposit?.enabled && selectedCode?.deposit?.validate) ? (
                <AffiliateAmountInput
                    selectedCode={selectedCode}
                    amount={amount ?? 0}
                    onChangeAmount={handleChangeAmount}
                />
            ) : null}

            {selectedCode?.affiliateNotification && (
                <AffiliateVoucherForm selectedCode={selectedCode} voucher={voucher} onChangeVoucher={handleVoucher} />
            )}

            {selectedCode?.type === CouponType.UNLOCK ? (
                <UnlockAmountInput amount={amount} onChange={handleUnlockAmountChange} />
            ) : null}

            {showMistSelection &&
                selectablePurchaseItems.map((purchaseItem) =>
                    purchaseItem.id && "arrivalDateTime" in purchaseItem ? (
                        <Checkbox
                            key={purchaseItem.id}
                            checked={selection.isSelected({ id: purchaseItem.id })}
                            name={purchaseItem.id}
                            disabled={
                                selectedCodeRestrictedProductIds.length > 0 &&
                                !selectedCodeRestrictedProductIds.includes(purchaseItem.productId)
                            }
                            label={
                                <ProductCheckboxContent
                                    productId={purchaseItem.productId}
                                    purchaseItem={purchaseItem}
                                />
                            }
                            className="px-3 py-2 font-bold"
                            classNames={{ checkbox: "!border !border-gray" }}
                            onChange={() => {
                                selection.select({ id: purchaseItem.id });
                            }}
                        />
                    ) : null,
                )}

            {shouldShowPrivacyWarning && selectedCode?.object === "affiliate" && (
                <div className="mt-4 flex w-full flex-col">
                    <Alert color="warning" className="text-left">
                        {purchaseItems.length > 1
                            ? "One or more of the experiences in this purchase no longer support the booked privacy option. Please change the privacy for those reservations if you would like the latest pricing to apply."
                            : "This experience no longer supports"}{" "}
                        {privacy} pricing. Please change the privacy for this reservation to{" "}
                        {(product as Experience)?.allowedPrivacies?.[0]} if you would like the latest pricing to apply.
                    </Alert>
                </div>
            )}
        </div>
    );
};

const ProductCheckboxContent = ({
    productId,
    purchaseItem,
}: {
    productId: ID;
    purchaseItem: Pick<CreatePurchaseItemState, "arrivalDateTime" | "arrivalTime">;
}) => {
    const { data: product, isLoading } = useProduct(productId);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <span className="flex items-center font-normal">
            {product && <ProductImage product={product} size="small" />}
            <div className="flex flex-col space-y-1">
                <NoTranslate className="font-bold text-black">{product?.name.trim()}</NoTranslate>
                {purchaseItem.arrivalDateTime ? (
                    <div>
                        <span>
                            <CalendarIcon className="mr-1" />
                            <span>{purchaseItem.arrivalDateTime}</span>
                        </span>
                        {purchaseItem.arrivalTime ? (
                            <span>
                                <ClockIcon className="mr-1" />
                                {formatDate(purchaseItem.arrivalDateTime, "LT")}
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </span>
    );
};

interface InputAmountProps {
    selectedCode: ApplyCode;
    amount: number;
    onChangeAmount: (value: number) => void;
}

const AffiliateAmountInput = ({ selectedCode, amount, onChangeAmount }: InputAmountProps) => {
    const isDisabled = selectedCode?.voucher && selectedCode?.deposit?.enabled && !selectedCode?.deposit?.validate;

    return (
        <FormGroup>
            <Label>{selectedCode.voucher ? "Voucher value" : "Agent deposit amount"}</Label>
            <Input
                disabled={isDisabled}
                min={0}
                type="number"
                value={amount ? Math.abs(amount) : ""}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    onChangeAmount(floor(Number.parseFloat(event.target.value), 2));
                }}
            />
        </FormGroup>
    );
};

interface UnlockAmountInputProps {
    amount: number | undefined;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const UnlockAmountInput = ({ amount, onChange }: UnlockAmountInputProps) => {
    return (
        <FormGroup>
            <Label isError={isNil(amount)}>{isNil(amount) ? "Amount is required" : "Amount"}</Label>
            <Input
                isError={isNil(amount)}
                min={0}
                type="number"
                value={amount ? Math.abs(amount) : null}
                onChange={onChange}
            />
        </FormGroup>
    );
};

const shouldIncludeCode = (
    code: AnyRewardCode,
    product: Product,
    primaryUnitLineItems: Array<Pick<DemographicLineItem, "quantity" | "template">>,
    quantity: number,
    purchaseItems: Array<{ id: ID; object: PurchaseItemObject; productId: ID; arrivalDateTime: string }>,
    isMembershipProduct: boolean,
    isMerchandisePurchase: boolean,
    user: User,
    purchaseItem?: ExperiencePurchaseItem | CreatePurchaseItemState,
    searchValue?: string,
    pkg?: Package /* eslint-disable-next-line max-params */,
): boolean => {
    if (isMembershipCode(code)) {
        return !isMembershipProduct && !!searchValue;
    }

    if (!code.coupon) return false;

    const { source, type, purchaseRule, permissions } = code.coupon;

    if ([CouponSource.GIFT, CouponSource.STORE_CREDIT].includes(source)) {
        return !!searchValue;
    }

    if (isMembershipProduct) {
        return false;
    }

    if (isMerchandisePurchase && [CouponType.UNLOCK, CouponType.BXGY].includes(type)) {
        return false;
    }

    const isRestrictedUser = user.type !== UserType.SELLER && !user.roles.includes(UserRole.ADMIN);

    // Check if the user's role is allowed in the coupon's permissions. Xola Admins and Sellers are always allowed.
    // If permissions is not defined or null, the code is applicable to all users.
    if (isRestrictedUser && Array.isArray(permissions)) {
        // If permissions is empty, the code is not applicable to any user except Admins and Sellers.
        if (permissions.length === 0) {
            return false;
        }

        // This is a special case for Seller Admins, as the API returns all delegate roles for a seller admin user
        if (user.roles.includes(DelegateRole.SELLER_ADMIN)) {
            return permissions.includes(DelegateRole.SELLER_ADMIN);
        }

        const hasRequiredRole = permissions.some((role) => user.roles.includes(role));
        if (!hasRequiredRole) {
            return false;
        }
    }

    if (type === CouponType.BXGY) {
        const item = purchaseItem ?? useCreatePurchaseStore.getState().getActiveItem();
        if (item && purchaseRule?.actions) {
            return canApplyBXGYCode(primaryUnitLineItems, quantity, purchaseRule, product);
        }

        return false;
    }

    if (pkg) {
        if (pkg?.allowedCouponTypes) {
            return (
                pkg.allowedCouponTypes.includes(code.coupon.type) ||
                [CouponSource.GIFT, CouponSource.STORE_CREDIT].includes(code.coupon.source)
            );
        }

        return [CouponSource.GIFT, CouponSource.STORE_CREDIT].includes(source);
    }

    if (purchaseRule?.filter) {
        const productRestrictions = getProductRestrictions(purchaseRule, "product.id");
        return productRestrictions.length > 0
            ? productRestrictions.includes(product?.id) ||
                  purchaseItems.some((item) => productRestrictions.includes(item?.productId))
            : true;
    }

    return true;
};
